// src/components/pages/Produto/modules/useEmbalagensModule.js
import { useCallback, useEffect, useMemo, useState } from 'react';

//APIs
import * as EmbalagemAPI from '../../../../api/embalagem';
import * as TabelaAPI from '../../../../api/tabela';

/**
 * Módulo de Embalagens do Produto (online/offline)
 * - Online: dados.id existe -> CRUD via API + listagem via API
 * - Offline: dados.id não existe -> CRUD local (array), com checagem de duplicidade local + duplicidade no backend (código barras)
 */
export function useEmbalagensModule({
    dados, // { id, empresaId }
    setDados,
    parametro,
    empresaId,
    setFlashMessage,
    //options e enums
    OptionsEmbalagem,
    OptionsEmpresa,
    // MRT state (centraliza aqui)
    mrt,

    // se quiser injetar uma listagem externa (opcional)
    // async ({ pagination, columnFilters, columnFilterFns, globalFilter, sorting, produtoId }) => ({ data, meta })
    listarEmbalagensApi,
}) {
    const produtoId = dados?.id ?? null;

    // extrai MRT state (evita undefined)
    const pagination = mrt?.pagination ?? { pageIndex: 0, pageSize: 5 };
    const columnFilters = mrt?.columnFilters ?? [];
    const columnFilterFns = mrt?.columnFilterFns ?? [];
    const globalFilter = mrt?.globalFilter ?? '';
    const sorting = mrt?.sorting ?? [];

    // grid
    const [items, setItems] = useState([]);
    const [rowCount, setRowCount] = useState(0);

    // loading flags do módulo
    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);
    const [isError, setIsError] = useState(false);

    const defaultNovaEmbalagem = useMemo(
        () => ({
            produtoId: produtoId ?? null,
            empresaId: dados?.empresaId ?? empresaId,
            embalagemId: null,
            uuid: '',
            codigoBarras: '',
            principal: false,
            aceitaFracao: false,
            ativo: true,
            funcao: 'Adicionar',
            index: null,
            Empresa: {},
            Embalagem: {},
        }),
        [produtoId, dados?.empresaId, empresaId]
    );

    const [novaEmbalagem, setNovaEmbalagem] = useState(defaultNovaEmbalagem);
    const [isDisabled, setIsDisabled] = useState(false);
    // mantém ids sempre sincronizados quando mudar produto/empresa
    useEffect(() => {
        setNovaEmbalagem((prev) => ({
            ...prev,
            produtoId: produtoId ?? null,
            // preserva empresa já escolhida; senão usa do produto/escopo
            empresaId: prev?.empresaId ?? dados?.empresaId ?? empresaId ?? null,
        }));
    }, [produtoId, dados?.empresaId, empresaId]);

    useEffect(() => {
        if (novaEmbalagem.codigoBarras || novaEmbalagem.embalagemId) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [novaEmbalagem.codigoBarras, novaEmbalagem.embalagemId]);

    // ---------- helpers internos ----------
    const syncOfflineItems = useCallback(
        (next) => {
            setItems(next);
            setRowCount(next.length);

            setDados((prev) => ({
                ...prev,
                produtoEmbalagens: next,
            }));
        },
        [setDados]
    );

    const limpar = useCallback(() => {
        setNovaEmbalagem((prev) => ({
            ...defaultNovaEmbalagem,
            // preserva empresa escolhida (boa UX)
            empresaId: prev?.empresaId ?? defaultNovaEmbalagem.empresaId,
        }));
    }, [defaultNovaEmbalagem]);

    const handleEditarOuDeletar = useCallback((originalRow, rowIndex, funcao) => {
        const newData = {
            uuid: originalRow.uuid,
            Embalagem: originalRow.Embalagem,
            Empresa: originalRow.Empresa,

            empresaId: originalRow.empresaId,
            produtoId: originalRow.produtoId,
            embalagemId: originalRow.embalagemId,
            codigoBarras: originalRow.codigoBarras,
            descricao: originalRow.descricao,

            principal: originalRow.principal,
            aceitaFracao: originalRow.aceitaFracao,
            ativo: originalRow.ativo,

            funcao,
            index: rowIndex,
        };

        setNovaEmbalagem(newData);
    }, []);

    // pega Empresa/Embalagem “visuais” pro modo offline
    const resolveEmpresaEEmbalagem = useCallback(async ({ empresaIdSel, embalagemIdSel }) => {
        const [empresa, embalagem] = await Promise.all([
            // compat com seu padrão: (empresaId, tabela, id)
            TabelaAPI.tabelaById(null, 'Empresa', empresaIdSel),
            TabelaAPI.tabelaById(empresaIdSel, 'Embalagem', embalagemIdSel),
        ]);

        return { empresa, embalagem };
    }, []);

    // ---------- API wrappers ----------
    const managerApi = useCallback(
        async (payload) => {
            try {
                const result = await EmbalagemAPI.managerProdutoEmbalagem(payload);
                setFlashMessage?.(result.message, result.type);
                return result;
            } catch (error) {
                console.error(error);
                if (error.response && error.response.status === 422) {
                    setFlashMessage?.('Erro de validação: ' + error.response.data.message, 'error');
                } else {
                    setFlashMessage?.('Ocorreu um erro ao processar a solicitação.', 'error');
                }
                throw error;
            }
        },
        [setFlashMessage]
    );

    const deletarApi = useCallback(
        async (payload) => {
            const resp = await EmbalagemAPI.managerProdutoEmbalagem(payload);
            setFlashMessage?.(resp.message, resp.type);
            return resp;
        },
        [setFlashMessage]
    );

    // serializa tableState pra dependência estável (evita refetch infinito)
    const tableStateKey = useMemo(() => {
        return JSON.stringify({
            pagination,
            columnFilters,
            columnFilterFns,
            globalFilter,
            sorting,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        pagination?.pageIndex,
        pagination?.pageSize,
        JSON.stringify(columnFilters),
        JSON.stringify(columnFilterFns),
        globalFilter,
        JSON.stringify(sorting),
    ]);

    // ---------- listagem ----------
    const refresh = useCallback(async () => {
        if (!produtoId) return;

        const hasItems = (items?.length ?? 0) > 0;
        hasItems ? setIsRefetching(true) : setIsLoading(true);

        try {
            setIsError(false);

            // 1) se vier externo, usa
            if (listarEmbalagensApi) {
                const r = await listarEmbalagensApi({
                    pagination,
                    columnFilters,
                    columnFilterFns,
                    globalFilter,
                    sorting,
                    produtoId,
                });
                setItems(r?.data ?? []);
                setRowCount(r?.meta?.totalRowCount ?? r?.data?.length ?? 0);
                return;
            }

            // 2) fallback padrão: chama API direto
            const response = await EmbalagemAPI.listarEmbalagens(
                (pagination.pageIndex ?? 0) * (pagination.pageSize ?? 5),
                pagination.pageSize ?? 5,
                JSON.stringify(columnFilters ?? []),
                JSON.stringify(columnFilterFns ?? []),
                globalFilter ?? '',
                JSON.stringify(sorting ?? []),
                produtoId
            );

            setItems(response?.data?.data ?? []);
            setRowCount(response?.data?.meta?.totalRowCount ?? response?.data?.data?.length ?? 0);
        } catch (e) {
            console.error(e);
            setIsError(true);
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }, [produtoId, listarEmbalagensApi, tableStateKey, items?.length]); // tableStateKey “puxa” as mudanças da tabela

    // auto-refresh quando estiver online e mudar estado da tabela
    useEffect(() => {
        if (!produtoId) return;
        refresh();
    }, [produtoId, refresh]);

    // ---------- ação principal ----------
    const submit = useCallback(async () => {
        // normaliza campos obrigatórios
        const payload = {
            ...novaEmbalagem,
            produtoId: novaEmbalagem.produtoId ?? produtoId ?? null,
            empresaId: novaEmbalagem.empresaId ?? dados?.empresaId ?? empresaId ?? null,
            embalagemId: novaEmbalagem.embalagemId ? Number(novaEmbalagem.embalagemId) : null,
            codigoBarras: String(novaEmbalagem.codigoBarras ?? '').trim(),
        };

        if (!payload.empresaId) {
            setFlashMessage?.('Selecione a empresa.', 'warning');
            return;
        }
        if (!payload.embalagemId) {
            setFlashMessage?.('Selecione a embalagem.', 'warning');
            return;
        }

        const funcao = (payload.funcao || 'Adicionar').toLowerCase();

        // ---------- ONLINE ----------
        if (produtoId) {
            // duplicidade de código barras (só na inclusão)
            if (funcao === 'adicionar' && payload.codigoBarras) {
                const dup = await EmbalagemAPI.duplicidadeCodigobarras(payload.empresaId, payload.codigoBarras);
                if (dup) {
                    setFlashMessage?.('Código de barras em duplicidade!', 'warning');
                    // seu comportamento atual: “carrega” a embalagem encontrada no form
                    handleEditarOuDeletar(dup, null, 'Adicionar');
                    return;
                }
            }

            if (funcao === 'deletar') {
                await managerApi(payload);
            } else {
                const result = await managerApi(payload);

                if (result?.status === 202) {
                    setFlashMessage?.('Embalagem já cadastrada!', 'info');
                } else if (result?.type === 'success' && Array.isArray(result?.resultado)) {
                    setItems(result.resultado);
                    setRowCount(result.resultado.length);
                }
            }

            await refresh();
            limpar();
            return;
        }

        // ---------- OFFLINE ----------
        // duplicidade local por código barras + empresa
        if (funcao === 'adicionar' && payload.codigoBarras) {
            const existeLocal = (items ?? []).some(
                (it) => it.codigoBarras === payload.codigoBarras && String(it.empresaId) === String(payload.empresaId)
            );
            if (existeLocal) {
                setFlashMessage?.('Código de barras já cadastrado para o produto!', 'warning');
                return;
            }

            // checa duplicidade global no backend (mesmo sem produtoId)
            const dup = await EmbalagemAPI.duplicidadeCodigobarras(payload.empresaId, payload.codigoBarras);
            if (dup) {
                setFlashMessage?.('Código de barras em duplicidade!', 'warning');
                return;
            }
        }

        const { empresa, embalagem } = await resolveEmpresaEEmbalagem({
            empresaIdSel: payload.empresaId,
            embalagemIdSel: payload.embalagemId,
        });

        if (funcao === 'editar') {
            if (payload.index != null) {
                const next = [...(items ?? [])];
                next[payload.index] = {
                    ...next[payload.index],
                    ...payload,
                    Empresa: empresa,
                    Embalagem: embalagem,
                };
                syncOfflineItems(next);
            }
        } else if (funcao === 'deletar') {
            if (payload.index != null) {
                const next = [...(items ?? [])];
                next.splice(payload.index, 1);
                syncOfflineItems(next);
            }
        } else {
            const uuidLocal =
                payload.uuid || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`);

            const novoItem = {
                uuid: uuidLocal,
                produtoId: payload.produtoId || null,
                empresaId: payload.empresaId,
                embalagemId: payload.embalagemId,
                codigoBarras: payload.codigoBarras,
                principal: !!payload.principal,
                aceitaFracao: !!payload.aceitaFracao,
                ativo: !!payload.ativo,
                Empresa: empresa,
                Embalagem: embalagem,
            };

            const next = [...(items ?? []), novoItem];
            syncOfflineItems(next);
        }

        limpar();
    }, [
        novaEmbalagem,
        produtoId,
        dados?.empresaId,
        empresaId,
        setFlashMessage,
        handleEditarOuDeletar,
        managerApi,
        deletarApi,
        refresh,
        limpar,
        items,
        resolveEmpresaEEmbalagem,
    ]);

    // -------------------------
    // change handler único (Form pode usar se quiser)
    // -------------------------
    const change = useCallback(
        async (e) => {
            const { name, value, type, checked } = e.target;
            const v = type === 'checkbox' ? !!checked : value;
            const campo = name.split('.')[1];
            // draft cor
            if (name.startsWith('novaEmbalagem.')) {
                setNovaEmbalagem((prev) => ({ ...prev, [campo]: v }));
                return;
            }
        },
        [setDados]
    );

    const focus = useCallback(() => {}, []);
    const blur = useCallback(() => {}, []);
    return {
        state: {
            dados,
            parametro,

            items,
            rowCount,
            isLoading,
            isRefetching,
            isError,
            novaEmbalagem,

            //options e enums
            OptionsEmbalagem,
            OptionsEmpresa,
            isDisabled,
        },
        actions: {
            change,
            focus,
            blur,
            limpar,
            submit,
            handleEditarOuDeletar,
            setNovaEmbalagem,
            refresh,
        },
    };
}
